import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite, mat, angleLerp } from '../core/meshkit.js';
import { breadShape } from './breadBoi.js';
import { isDaytime, isNight } from '../core/world.js';
import { questsForGiver } from '../core/quests.js';

// The townsfolk of Toast Town — shared NPC class + helpers. The actual cast is
// now spawned from entities/characters.js via entities/npc.js, but the
// Townsperson lifecycle (idle/wander/roam, speech bubble, quest indicator)
// lives here so every scene uses one consistent behavior.

// ---- shared bits --------------------------------------------------------

const CHAR = 0x2E2018;   // the signature dark line/eye ink used across the cast

export function stickLegs(g, len = 0.5, spread = 0.18, color = C.limb){
  cyl(.05, .05, len, color, -spread, len / 2, 0, g, 6);
  cyl(.05, .05, len, color,  spread, len / 2, 0, g, 6);
  box(.17, .09, .26, C.crustDark, -spread, .05, .06, g);   // shoes
  box(.17, .09, .26, C.crustDark,  spread, .05, .06, g);
}

function torus(r, tube, color, x, y, z, g, seg = 16){
  const m = new THREE.Mesh(new THREE.TorusGeometry(r, tube, 8, seg), mat(color));
  m.position.set(x, y, z);
  m.castShadow = true;
  g.add(m);
  return m;
}

// a little slice body (for the bread-kind cast), reusing Bread Boi's silhouette
export function breadSlice(g, crustColor, crumbColor, scale = 0.6, y = 1.1){
  const s = new THREE.Group();
  s.position.set(0, y, 0);
  s.scale.setScalar(scale);
  g.add(s);
  const crust = new THREE.Mesh(
    new THREE.ExtrudeGeometry(breadShape(1),
      { depth:.4, bevelEnabled:true, bevelSize:.05, bevelThickness:.05, bevelSegments:2 }),
    mat(crustColor));
  crust.position.z = -.22; crust.castShadow = true; s.add(crust);
  const inner = new THREE.Mesh(
    new THREE.ExtrudeGeometry(breadShape(.88), { depth:.44, bevelEnabled:false }),
    mat(crumbColor));
  inner.position.z = -.21; s.add(inner);
  return s;
}

function roundRect(g, x, y, w, h, r){
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y,     x + w, y + h, r);
  g.arcTo(x + w, y + h, x,     y + h, r);
  g.arcTo(x,     y + h, x,     y,     r);
  g.arcTo(x,     y,     x + w, y,     r);
  g.closePath();
}

// word-wrapped speech bubble sprite (drawn on a canvas, faces the camera)
function speechBubble(text){
  const cv = document.createElement('canvas');
  cv.width = 512; cv.height = 256;
  const g = cv.getContext('2d');
  const pad = 30, font = 34, maxW = cv.width - pad * 2;
  g.font = `600 ${font}px "Space Grotesk", system-ui, sans-serif`;

  const words = text.split(' ');
  const lines = []; let cur = '';
  for (const w of words){
    const t = cur ? cur + ' ' + w : w;
    if (g.measureText(t).width > maxW && cur){ lines.push(cur); cur = w; }
    else cur = t;
  }
  if (cur) lines.push(cur);

  const lh = font * 1.25;
  const bh = pad * 2 + lines.length * lh;
  const y0 = (cv.height - bh) / 2 - 12;

  g.fillStyle = 'rgba(255,246,222,0.97)';
  g.strokeStyle = '#C79A5B'; g.lineWidth = 4;
  roundRect(g, 10, y0, cv.width - 20, bh, 24); g.fill(); g.stroke();
  // tail
  g.beginPath();
  g.moveTo(cv.width / 2 - 18, y0 + bh - 2);
  g.lineTo(cv.width / 2 + 18, y0 + bh - 2);
  g.lineTo(cv.width / 2, y0 + bh + 26);
  g.closePath(); g.fill();

  g.fillStyle = '#3A2A1C';
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.font = `600 ${font}px "Space Grotesk", system-ui, sans-serif`;
  lines.forEach((ln, i) => g.fillText(ln, cv.width / 2, y0 + pad + lh / 2 + i * lh));

  const tex = new THREE.CanvasTexture(cv);
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  const h = 1.5;
  sp.scale.set(h * (cv.width / cv.height), h, 1);
  sp.renderOrder = 999;
  return sp;
}

// ---- the townsperson ----------------------------------------------------

export class Townsperson {
  constructor(spec){
    this.id = spec.id || null;
    this.root = new THREE.Group();
    this.root.position.copy(spec.pos);
    this.home = spec.pos.clone();
    this.facing = spec.facing ?? 0;
    this.root.rotation.y = this.facing;

    this.speed  = spec.speed  ?? 1.2;
    this.wander = spec.wander ?? 0;         // 0 = stays put (unless roam)
    this.roam   = spec.roam   ?? false;     // travel between town waypoints
    this.patrol = spec.patrol ?? false;     // loop waypoints in order
    this.patrolIndex = 0;
    this.schedule = spec.schedule ?? 'always'; // 'always' | 'day' | 'night' | 'never' | 'special'
    this.quadruped = spec.quadruped || false;
    this.slow = spec.slow || false;
    this.giver = spec.giver || null;          // quest-giver NPC id (e.g. 'mayor')
    this.phase  = Math.random() * Math.PI * 2;
    this.target = null;
    this._lastWp = null;
    this.pauseT = 0.5 + Math.random() * 2.5;

    this.body = new THREE.Group();
    this.root.add(this.body);
    spec.build(this.body);                   // builder fills the body group

    this.bubble = speechBubble(spec.line);
    this.bubble.position.set(0, spec.bubbleY ?? 2.8, 0);
    this.bubble.material.opacity = 0;
    this.bubble.visible = false;
    this.root.add(this.bubble);

    // invisible click target so players can tap the NPC to speak
    this.hitTarget = new THREE.Mesh(
      new THREE.CylinderGeometry(0.7, 0.7, 2.2, 12),
      new THREE.MeshBasicMaterial({ visible: false })
    );
    this.hitTarget.position.set(0, 1.1, 0);
    this.hitTarget.userData.npc = this;
    this.root.add(this.hitTarget);

    // small quest-available indicator above the head
    this.indicator = textSprite('!', 40, '#E23B4E', 0.5, 0.7);
    this.indicator.position.set(0, (spec.bubbleY ?? 2.8) + 0.6, 0);
    this.indicator.visible = false;
    this.root.add(this.indicator);

    this._bubbleTimer = 0;
    this._facingPlayer = false;
    this._indicatorVisible = false;
  }

  showLine(duration = 2.2){
    this._bubbleTimer = duration;
    this.bubble.visible = true;
    this.bubble.material.opacity = 1;
    this._facingPlayer = true;
  }

  hideLine(){
    this._bubbleTimer = 0;
    this.bubble.material.opacity = 0;
    this.bubble.visible = false;
    this._facingPlayer = false;
  }

  _pickTarget(){
    if (this.patrol){
      // follow the waypoint loop in order
      this.target = WAYPOINTS[this.patrolIndex].clone();
      this.patrolIndex = (this.patrolIndex + 1) % WAYPOINTS.length;
    } else if (this.roam){
      // travel to a fresh spot somewhere across town
      let wp = this._lastWp;
      while (wp === this._lastWp && WAYPOINTS.length > 1){
        wp = WAYPOINTS[(Math.random() * WAYPOINTS.length) | 0];
      }
      this._lastWp = wp;
      this.target = wp.clone();
    } else {
      // amble within the home radius
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * this.wander;
      this.target = this.home.clone().add(new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r));
    }
  }

  update(dt, breadPos){
    // quest-giver indicator
    if (this.giver){
      const hasAvailable = questsForGiver(this.giver).available.length > 0;
      if (hasAvailable !== this._indicatorVisible){
        this._indicatorVisible = hasAvailable;
        this.indicator.visible = hasAvailable;
      }
    }

    // speech-bubble timer (click-to-show)
    if (this._bubbleTimer > 0){
      this._bubbleTimer -= dt;
      if (this._bubbleTimer <= 0) this.hideLine();
    }

    if (this._facingPlayer){
      // turn to face Bread Boi while the bubble is up
      this.target = null;
      const to = new THREE.Vector3().subVectors(breadPos, this.root.position);
      const want = Math.atan2(to.x, to.z);
      this.facing = angleLerp(this.facing, want, Math.min(1, dt * 5));
      this.root.rotation.y = this.facing;
      this._idle(dt);
      return;
    }

    if (this.wander <= 0 && !this.roam && !this.patrol){ this._idle(dt); return; }

    // amble/travel to a point, pause, pick another
    if (!this.target){
      this.pauseT -= dt;
      if (this.pauseT <= 0) this._pickTarget();
      this._idle(dt);
      return;
    }
    const dir = new THREE.Vector3().subVectors(this.target, this.root.position); dir.y = 0;
    const dist = dir.length();
    if (dist < 0.15){
      this.target = null;
      this.pauseT = 1 + Math.random() * 2.5;      // loiter, then move on
      return;
    }
    dir.normalize();
    const want = Math.atan2(dir.x, dir.z);
    this.facing = angleLerp(this.facing, want, Math.min(1, dt * 5));
    this.root.rotation.y = this.facing;
    this.root.position.addScaledVector(dir, this.speed * dt);
    this._step(dt);
  }

  _idle(dt){ this.phase += dt * (this.slow ? 1 : 2); this.body.position.y = Math.sin(this.phase) * 0.03; }
  _step(dt){ this.phase += dt * (this.quadruped ? 12 : this.slow ? 5 : 10); this.body.position.y = Math.abs(Math.sin(this.phase)) * (this.quadruped ? 0.05 : 0.08); }

  shouldSpawn(){
    if (this.schedule === 'never' || this.schedule === 'special') return false;
    if (this.schedule === 'always') return true;
    if (this.schedule === 'day') return isDaytime();
    if (this.schedule === 'night') return isNight();
    return true;
  }
}

// Helper: raycast against the invisible click targets of one or more NPCs.
// Returns true and triggers the NPC's speech bubble if one was clicked.
export function checkNpcClick(ray, candidates, bread){
  const list = (Array.isArray(candidates) ? candidates : [candidates]).filter(Boolean);
  const targets = list
    .filter(n => n.root && n.root.visible && n.hitTarget)
    .map(n => n.hitTarget);
  if (!targets.length) return false;
  const hits = ray.intersectObjects(targets, false);
  if (hits.length){
    const npc = hits[0].object.userData.npc;
    if (npc){ if (bread) bread.stop(); npc.showLine(); return true; }
  }
  return false;
}

// Places roamers travel between — building fronts, benches, and plaza spots.
// Updated for the Compass Plan: fountain radius ~3.2, building ring 17, gates 28.
const WAYPOINTS = [
  // building fronts (radius ~14-15)
  new THREE.Vector3(-4.7, 0, -11.3), new THREE.Vector3(4.7, 0, -11.3),      // house / comics
  new THREE.Vector3(12.3, 0, -5.1),  new THREE.Vector3(12.3, 0, 5.1),       // council / court
  new THREE.Vector3(4.7, 0, 11.3),   new THREE.Vector3(-4.7, 0, 11.3),      // grill / café
  // plaza rim spots (radius ~7-8)
  new THREE.Vector3(0, 0, -7.5),     new THREE.Vector3(0, 0, 7.5),
  new THREE.Vector3(7.5, 0, 0),      new THREE.Vector3(-7.5, 0, 0),
  new THREE.Vector3(5.3, 0, 5.3),    new THREE.Vector3(-5.3, 0, 5.3),
  new THREE.Vector3(5.3, 0, -5.3),   new THREE.Vector3(-5.3, 0, -5.3)
];
